"use client";

import { usePathname } from "next/navigation";
import React, { useEffect, useState } from "react";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { useSidebar } from "@/components/ui/sidebar";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { ExportStatusProvider } from "../export/export-status-context";
import { isAiPagePathname } from "./ai-pathname";
import { useAiSidePanelVisibility } from "./ai-side-panel-visibility";
import DemoAlert from "./demo-alert";
import {
  AI_PANEL_DEFAULT_SIZE,
  AI_PANEL_MAX_SIZE,
  AI_PANEL_MIN_SIZE,
  SIDEBAR_MIN_SIZE,
  useWorkspacePanelLayout,
} from "./workspace-panel-layout";
import { WorkspaceSidePanelContent } from "./workspace-side-panel-content";

type WorkspaceViewProps = {
  sidebar: React.ReactNode;
  header: React.ReactNode;
  children: React.ReactNode;
  defaultLayout?: number[];
};

const DEFAULT_LAYOUT: [number, number] = [20, 80];
type AiSidePanelResizableContentProps = {
  isOpen: boolean;
  defaultSize: number;
};

function AiSidePanelResizableContent({
  isOpen,
  defaultSize,
}: AiSidePanelResizableContentProps) {
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  return (
    <>
      <ResizableHandle
        withHandle
        className={cn(!isOpen && "pointer-events-none opacity-0")}
      />
      <ResizablePanel
        id="workspace-view-ai-panel"
        order={3}
        defaultSize={defaultSize}
        collapsible
        collapsedSize={0}
        minSize={AI_PANEL_MIN_SIZE}
        maxSize={AI_PANEL_MAX_SIZE}
        className={cn(
          "relative z-20 overflow-hidden bg-background",
          !isOpen && "border-0",
        )}
      >
        <div hidden={!isOpen} aria-hidden={!isOpen} className="h-full">
          {/*
            保存済み設定を読む chat subtree は hydration 後に一度だけ mount し、
            panel の開閉では破棄しない。未送信 draft や実行中 stream を保持するため。
          */}
          {hasMounted ? <WorkspaceSidePanelContent /> : null}
        </div>
      </ResizablePanel>
    </>
  );
}

export function WorkspaceView({
  sidebar,
  header,
  children,
  defaultLayout = DEFAULT_LAYOUT,
}: WorkspaceViewProps) {
  const { open } = useSidebar();
  const isMobile = useIsMobile();
  const { isVisible: isAiPanelVisible } = useAiSidePanelVisibility();
  const pathname = usePathname();
  const pathSegments = pathname?.split("/").filter(Boolean) ?? [];
  const isAiPage = isAiPagePathname(pathname);
  const isWorkspaceSettingsPage = pathSegments[2] === "settings";
  const isAnalyticsPage =
    ["ua", "sa", "ads", "iw"].includes(pathSegments[2] ?? "") &&
    !pathSegments.includes("settings");
  const { panelGroupRef, layout, onLayout } = useWorkspacePanelLayout({
    defaultLayout,
    isSidebarOpen: open,
    isAiPanelOpen: isAiPanelVisible,
  });

  useEffect(() => {
    const previousMask = document.body.getAttribute("data-clarity-mask");

    if (!isWorkspaceSettingsPage) {
      return undefined;
    }

    document.body.setAttribute("data-clarity-mask", "true");

    return () => {
      if (previousMask === null) {
        document.body.removeAttribute("data-clarity-mask");
        return;
      }
      document.body.setAttribute("data-clarity-mask", previousMask);
    };
  }, [isWorkspaceSettingsPage]);

  return (
    <div className="h-screen overflow-hidden flex w-full print:h-auto">
      <ResizablePanelGroup
        // react-resizable-panelsのSSR系の問題？
        // ランタイムエラー防止のため、明示的にid propsを指定
        // workspace-view自体は1個しかレンダリングされないので、固定IDで良いとする
        // https://linear.app/jadekk/issue/AME-1765
        id="workspace-view-resizable-panel-group"
        ref={panelGroupRef}
        direction="horizontal"
        className="w-full h-full"
        onLayout={onLayout}
      >
        <ResizablePanel
          id="workspace-view-sidebar-panel"
          order={1}
          defaultSize={layout[0]}
          minSize={open ? SIDEBAR_MIN_SIZE : 0}
          maxSize={isMobile ? 0 : undefined}
          className={cn(
            "transition-none",
            open && "md:min-w-[256px] md:max-w-[512px]",
            !open && "hidden",
            "print:hidden print:min-w-0 print:w-0",
          )}
        >
          {sidebar}
        </ResizablePanel>
        <ResizableHandle className={cn(!open && "hidden", "print:hidden")} />
        <ResizablePanel
          id="workspace-view-main-panel"
          order={2}
          defaultSize={layout[1]}
          className="relative z-0 print:w-full print:max-w-full"
          data-memo-tooltip-root=""
        >
          <div className={cn("flex flex-col h-full")}>
            <ExportStatusProvider>
              {header}
              <main
                data-page-sticky-tables={isAnalyticsPage ? "" : undefined}
                className={cn(
                  "flex-1 overflow-y-auto min-h-0 w-full print:overflow-visible print:p-0",
                  isAiPage || isAnalyticsPage ? "p-0" : "p-4 md:px-6 md:pb-6",
                )}
              >
                {isAnalyticsPage ? (
                  <div className="p-4 md:px-6 md:pb-6">{children}</div>
                ) : (
                  children
                )}
              </main>
            </ExportStatusProvider>
          </div>
        </ResizablePanel>

        <AiSidePanelResizableContent
          isOpen={isAiPanelVisible}
          defaultSize={layout[2] ?? AI_PANEL_DEFAULT_SIZE}
        />
      </ResizablePanelGroup>
      <DemoAlert />
    </div>
  );
}
